import { useLayoutStore } from '@/store/layout/layoutStore';
import { computed, inject, Ref, ref } from 'vue';
import { useFormStore } from '@/store/form/formStore';
import { useOrderStore } from '@/store/order/orderStore';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { useCloseOrderButton } from '@/composables/useCloseOrderButton';
import { useValidation } from '@/composables/useValidation';
import { ANALYTICS_PROVIDER } from '@/plugins/bxAnalytics';
import { generateUniqueId } from '@/helpers/uuidUtils';
import { Order } from '@/models/Order';
import ImageCapture, {
  ImageCaptureRef,
} from '@/components/Forms/DeviceFormComponents/ImageCapture.vue';

export function useOrderForm({
  imageCaptureRef,
}: {
  imageCaptureRef: Ref<ImageCaptureRef | null>;
}) {
  const orderStore = useOrderStore();
  const buildingStore = useBuildingStore();
  const formStore = useFormStore();
  const layoutStore = useLayoutStore();

  const { finishOrder, order, initializeOrderForm } = useCloseOrderButton(
    orderStore.activeOrder
  );

  const hasAnyChanges = computed(() => formStore.hasFormChanges);

  const { v$, isFormValid } = useValidation({ order });
  const validateForm = () => formStore.triggerValidation();

  const analytics = inject(ANALYTICS_PROVIDER);
  const formId = generateUniqueId();
  const trackOpen = () => analytics?.captureEvent('open_form', { formId });
  const trackSave = (o: Order) =>
    analytics?.captureEvent('save_form', {
      formId,
      entityId: o.id,
      entityType: 'order',
      hasFormChanges: hasAnyChanges.value,
    });

  const isPropertyCompatible = computed(
    () => order.value.propertyCompatibility?.isCompatible
  );

  const handleIsPropertyCompatible = (isCompatible: boolean) => {
    if (isCompatible && order.value.propertyCompatibility) {
      order.value.propertyCompatibility.reason = undefined;
      order.value.propertyCompatibility.note = undefined;
    }
    formStore.resetValidation();
  };

  const isCancelOpen = computed(() => layoutStore.isCancelFormPromptOpen);
  const isMissingInfoOpen = ref(false);
  const isCompatibilityOpen = ref(false);

  const openCancel = () => {
    if (!hasAnyChanges.value) {
      closeForm();
      return;
    }
    layoutStore.setCancelPromptState(true);
  };

  const closeCancel = () => layoutStore.setCancelPromptState(false);
  const closeForm = () => layoutStore.setOrderFormState(false);

  const openPrompt = async () => {
    if (
      buildingStore.hasUnmatchedOrderAddresses() &&
      isPropertyCompatible.value
    ) {
      isMissingInfoOpen.value = true;
      return;
    }
    if (!isPropertyCompatible.value && isFormValid.value) {
      isCompatibilityOpen.value = true;
      return;
    }
    if (!isFormValid.value) {
      validateForm();
      return;
    }
    await handleSubmit();
  };

  const closeMissingInfo = () => (isMissingInfoOpen.value = false);
  const proceedMissingInfo = () => {
    isMissingInfoOpen.value = false;
    closeForm();
  };

  const closeCompatibility = () => (isCompatibilityOpen.value = false);
  const proceedCompatibility = async () => {
    isCompatibilityOpen.value = false;
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (!isFormValid.value) {
      validateForm();
      return;
    }
    try {
      await imageCaptureRef.value?.saveImage?.();
    } catch (e) {
      console.error('image upload failed', e);
    }
    await finishOrder();
    trackSave(order.value);
  };

  return {
    order,
    v$,
    isFormValid,
    hasAnyChanges,
    isPropertyCompatible,
    isCancelOpen,
    isMissingInfoOpen,
    isCompatibilityOpen,
    openCancel,
    closeCancel,
    openPrompt,
    closeMissingInfo,
    proceedMissingInfo,
    closeCompatibility,
    proceedCompatibility,
    handleIsPropertyCompatible,
    validateForm,
    handleSubmit,
    closeForm,
    initializeOrderForm,
    trackOpen,
  };
}
